/**
 * The right panel's data, through the registry. Third entry in the refetch set.
 *
 * ⛔ SAME RULE AS `useConversations` AND `useThread`: a component calling `fetch`
 * directly never enters the query store, so `refetchAll()` cannot re-run it, and
 * it goes stale only after a reconnect nobody watched.
 *
 * ⚠️ AND THIS IS THE ENTRY THAT MAKES THE STORE'S OWN ARGUMENT CHECKABLE FROM A
 * THIRD ANGLE. `query-store.ts` rejects a narrow refetch because *"the event
 * most likely to be missed is a NEW CONVERSATION APPEARING IN A LIST NOBODY IS
 * LOOKING AT"*. With three keys mounted at once - list, thread, panel - a
 * refetch implemented as "the open conversation" is now wrong in two places
 * rather than one.
 */
import { useEffect, useState } from 'react'
import { subscribe } from '../data/query-store.js'
import { type PanelResponse, fetchPanel } from '../data/inbox-api.js'
import type { ConversationPanel } from '../../../src/services/conversation-panel.js'

export type UsePanel = {
  /**
   * `undefined` = no conversation open. `null` = asked, and there is nothing -
   * absent or out of scope, which are one answer by construction.
   *
   * ⛔ THREE STATES AND THEY ARE NOT TWO. The component renders differently for
   * each, because "you have not opened anything" and "that conversation is not
   * there" are different sentences and only one of them is about the data.
   */
  panel: ConversationPanel | null | undefined
  /**
   * ⛔ SEE `useThread`. `RightPanel` renders *"Could not load this customer"*
   * behind an `error` prop, and until 2026-08-08 nothing could set it - the
   * third instance of the same defect, in the third component written by
   * copying the first.
   */
  error: string | null
}

export function usePanel(conversationId: string | null): UsePanel {
  const [panel, setPanel] = useState<ConversationPanel | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Cleared on every change, for the reason `useThread` carries the same
    // line: B's panel must not render under A's heading while B is in flight,
    // and here the two panels are two different PEOPLE.
    setPanel(undefined)
    setError(null)
    if (conversationId === null) return

    return subscribe<PanelResponse>(
      `panel:${conversationId}`,
      () => fetchPanel(conversationId),
      (res) => {
        setPanel(res.panel)
        setError(null)
      },
      (err) => setError(err instanceof Error ? err.message : 'Unknown error'),
    )
  }, [conversationId])

  return { panel, error }
}
